import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { green } from '@mui/material/colors';
import {
    ApplicableMetricLevel,
    createReviewEntityMetricLevelInstance,
    deleteReviewEntityMetricLevelInstance,
    getReviewEntityMetricLevelConfig,
    MetricLevelConfig,
    MetricLevelInstance,
    MetricLevelInstanceInput,
    updateReviewEntityApplicableMetricLevels,
    updateReviewEntityMetricLevelInstance
} from '../../services/reviewEntityMetricLevelService';
import { getApiErrorMessage } from '../../services/api';
import MetricLevelHierarchyGraph from './MetricLevelHierarchyGraph';
import MetricsTemplateDownloadDialog from '../Metrics/MetricsTemplateDownloadDialog';

interface TypeMetricLevel {
    id: number;
    name: string;
    display_label: string;
    is_period_based: boolean;
    level_number: number;
}

interface ReviewEntityMetricLevelsPanelProps {
    entityId: number;
    entityName: string;
    typeMetricLevels: TypeMetricLevel[];
}

interface InstanceFormState {
    id?: string;
    metric_level_id: number | '';
    name: string;
    start_date: string;
    end_date: string;
    parent_instance_id: string;
}

const emptyForm = (): InstanceFormState => ({
    metric_level_id: '',
    name: '',
    start_date: '',
    end_date: '',
    parent_instance_id: ''
});

const ReviewEntityMetricLevelsPanel: React.FC<ReviewEntityMetricLevelsPanelProps> = ({
    entityId,
    entityName,
    typeMetricLevels
}) => {
    const [config, setConfig] = useState<MetricLevelConfig | null>(null);
    const [selectedLevelNames, setSelectedLevelNames] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingLevels, setSavingLevels] = useState(false);
    const [levelsSaved, setLevelsSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState<'add' | 'edit'>('add');
    const [form, setForm] = useState<InstanceFormState>(emptyForm());
    const [instanceSaving, setInstanceSaving] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

    const loadConfig = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getReviewEntityMetricLevelConfig(entityId);
            setConfig(data);
            setSelectedLevelNames(data.metric_level_names || []);
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Failed to load metric level config'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId]);

    /**
     * Autocomplete options must stay available even when entity-type details are missing
     * on the parent payload. The hierarchy graph already falls back to
     * config.applicable_metric_levels — the dropdown must use the same catalog.
     */
    const levelOptions = useMemo((): TypeMetricLevel[] => {
        if (typeMetricLevels.length > 0) {
            return typeMetricLevels;
        }
        const fromConfig = config?.applicable_metric_levels || [];
        if (fromConfig.length > 0) {
            return fromConfig.map(level => ({
                id: level.id,
                name: level.name,
                display_label: level.display_label,
                is_period_based: Boolean(level.is_period_based),
                level_number: level.level_number,
            }));
        }
        return [];
    }, [typeMetricLevels, config]);

    // Hydrate Autocomplete selection from config ids/names, or default to the type catalog
    // when the entity has no saved levels yet (keeps UI aligned with the hierarchy graph).
    useEffect(() => {
        if (!config) return;
        if (selectedLevelNames.length > 0) return;
        if (levelOptions.length === 0) return;

        const ids = (config.applicable_metric_level_ids || []).map(Number).filter(n => !Number.isNaN(n));
        if (ids.length > 0) {
            const idSet = new Set(ids);
            const namesFromIds = levelOptions.filter(l => idSet.has(l.id)).map(l => l.name);
            if (namesFromIds.length > 0) {
                setSelectedLevelNames(namesFromIds);
                return;
            }
        }

        const namesFromApi = (config.metric_level_names || []).filter(Boolean);
        if (namesFromApi.length > 0) {
            const optionNames = new Set(levelOptions.map(l => l.name));
            const matched = namesFromApi.filter(n => optionNames.has(n));
            if (matched.length > 0) {
                setSelectedLevelNames(matched);
                return;
            }
        }

        // Entity has no saved applicable levels yet — preselect the type/catalog levels so
        // the dropdown is populated and Add Instance sections appear (still need Save).
        setSelectedLevelNames(levelOptions.map(l => l.name));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, levelOptions]);

    const selectedTypeLevels = useMemo(() => {
        const selected = new Set(selectedLevelNames);
        return levelOptions.filter(level => selected.has(level.name));
    }, [selectedLevelNames, levelOptions]);

    /** True when the dropdown is showing type/catalog defaults that are not yet persisted on this entity. */
    const showingUnsavedDefaultLevels = useMemo(() => {
        if (!config || selectedLevelNames.length === 0) return false;
        const savedIds = config.applicable_metric_level_ids || [];
        return savedIds.length === 0;
    }, [config, selectedLevelNames]);

    /** True when current Autocomplete selection differs from what is saved on the entity. */
    const hasUnsavedLevelChanges = useMemo(() => {
        if (!config) return false;
        const saved = new Set(config.metric_level_names || []);
        if (saved.size !== selectedLevelNames.length) return true;
        return selectedLevelNames.some(name => !saved.has(name));
    }, [config, selectedLevelNames]);
    const handleSaveApplicableLevels = async () => {
        if (
            selectedLevelNames.length === 0 &&
            (config?.instances || []).length > 0
        ) {
            setError(
                'Cannot clear all levels while instances exist. Remove instances first, or keep at least one level selected.'
            );
            return;
        }
        setSavingLevels(true);
        setError(null);
        try {
            const data = await updateReviewEntityApplicableMetricLevels(entityId, selectedLevelNames);
            setConfig(data);
            setSelectedLevelNames(data.metric_level_names || selectedLevelNames);
            setLevelsSaved(true);
            setTimeout(() => setLevelsSaved(false), 2000);
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Failed to save applicable levels'));
        } finally {
            setSavingLevels(false);
        }
    };

    const openAddDialog = (level?: ApplicableMetricLevel | TypeMetricLevel) => {
        setEditMode('add');
        setDialogError(null);
        setForm({
            ...emptyForm(),
            metric_level_id: level?.id ?? ''
        });
        setDialogOpen(true);
    };

    const openEditDialog = (instance: MetricLevelInstance) => {
        setEditMode('edit');
        setDialogError(null);
        setForm({
            id: instance.id,
            metric_level_id: instance.metric_level_id,
            name: instance.name,
            start_date: instance.start_date || '',
            end_date: instance.end_date || '',
            parent_instance_id: instance.parent_instance_id || ''
        });
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setDialogError(null);
    };

    const selectedFormLevel = useMemo(() => {
        if (form.metric_level_id === '') return null;
        return (
            levelOptions.find(l => l.id === form.metric_level_id) ||
            config?.applicable_metric_levels.find(l => l.id === form.metric_level_id) ||
            null
        );
    }, [form.metric_level_id, levelOptions, config]);
    const parentOptions = useMemo(() => {
        if (!config || !selectedFormLevel) return [];
        const childRank = selectedFormLevel.level_number;
        const higherLevelIds = new Set(
            (config.applicable_metric_levels || [])
                .filter(l => l.level_number > childRank)
                .map(l => l.id)
        );
        return (config.instances || []).filter(
            inst =>
                higherLevelIds.has(inst.metric_level_id) &&
                inst.id !== form.id
        );
    }, [config, selectedFormLevel, form.id]);

    const handleSaveInstance = async () => {
        if (form.metric_level_id === '' || !form.name.trim()) {
            setDialogError('Level and name are required');
            return;
        }
        if (
            selectedFormLevel?.is_period_based &&
            form.start_date &&
            form.end_date &&
            form.end_date < form.start_date
        ) {
            setDialogError('End date must be on or after start date');
            return;
        }
        const payload: MetricLevelInstanceInput = {
            id: form.id,
            metric_level_id: Number(form.metric_level_id),
            name: form.name.trim(),
            start_date: form.start_date || null,
            end_date: form.end_date || null,
            parent_instance_id: form.parent_instance_id || null
        };
        setInstanceSaving(true);
        setDialogError(null);
        try {
            const data =
                editMode === 'edit' && form.id
                    ? await updateReviewEntityMetricLevelInstance(entityId, form.id, payload)
                    : await createReviewEntityMetricLevelInstance(entityId, payload);
            setConfig(data);
            setSelectedLevelNames(data.metric_level_names || []);
            closeDialog();
        } catch (err: unknown) {
            setDialogError(getApiErrorMessage(err, 'Failed to save instance'));
        } finally {
            setInstanceSaving(false);
        }
    };

    const handleDeleteInstance = async (instance: MetricLevelInstance) => {
        if (!window.confirm(`Delete instance "${instance.name}"?`)) return;
        setError(null);
        try {
            const data = await deleteReviewEntityMetricLevelInstance(entityId, instance.id);
            setConfig(data);
            setSelectedLevelNames(data.metric_level_names || []);
        } catch (err: unknown) {
            setError(getApiErrorMessage(err, 'Failed to delete instance'));
        }
    };

    if (levelOptions.length === 0 && !loading) {
        return (
            <Grid item xs={12}>
                <Card sx={{ bgcolor: 'grey.50' }}>
                    <CardHeader
                        title="Metric Levels"
                        subheader="No metric levels are configured on this entity type. Configure them under Organization Setup → Entity Types."
                        action={
                            <IconButton
                                onClick={() => setExpanded(prev => !prev)}
                                aria-expanded={expanded}
                                sx={{
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s'
                                }}
                            >
                                <ExpandMoreIcon />
                            </IconButton>
                        }
                    />
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Divider />
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                No metric levels are configured on this entity type.
                            </Typography>
                        </CardContent>
                    </Collapse>
                </Card>
            </Grid>
        );
    }
    return (
        <Grid item xs={12}>
            <Card sx={{ bgcolor: 'grey.50' }}>
                <CardHeader
                    title="Metric Levels"
                    subheader={
                        expanded
                            ? 'Choose a subset of the entity type\'s levels, then maintain instances (e.g. Sprint 5 under Milestone M1).'
                            : (() => {
                                const levelCount =
                                    config?.applicable_metric_levels?.length ||
                                    config?.applicable_metric_level_ids?.length ||
                                    0;
                                const instanceCount = config?.instances?.length ?? 0;
                                return `${levelCount} level(s), ${instanceCount} instance(s)`;
                            })()
                    }
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {expanded && levelsSaved && <CheckCircleIcon sx={{ color: green[500] }} />}
                            {expanded && (
                                <Button
                                    startIcon={<DownloadIcon />}
                                    variant="outlined"
                                    onClick={() => setTemplateDialogOpen(true)}
                                    disabled={loading}
                                >
                                    Metrics Template
                                </Button>
                            )}
                            {expanded && (
                                <Button
                                    startIcon={<SaveIcon />}
                                    variant="contained"
                                    color={showingUnsavedDefaultLevels || hasUnsavedLevelChanges ? 'warning' : 'primary'}
                                    onClick={handleSaveApplicableLevels}
                                    disabled={savingLevels || loading}
                                >
                                    {savingLevels ? 'Saving...' : 'Save Levels'}
                                </Button>
                            )}
                            <IconButton
                                onClick={() => setExpanded(prev => !prev)}
                                aria-expanded={expanded}
                                aria-label="toggle metric levels"
                                sx={{
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s'
                                }}
                            >
                                <ExpandMoreIcon />
                            </IconButton>
                        </Box>
                    }
                />
                <Divider />
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <>
                            <Autocomplete
                                multiple
                                options={levelOptions}
                                getOptionLabel={(option) =>
                                    `L${option.level_number}: ${option.display_label} (${option.name})`
                                }
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={levelOptions.filter(l => selectedLevelNames.includes(l.name))}
                                onChange={(_, newValue) => {
                                    // Preserve catalog order for selected subset
                                    const selectedIds = new Set(newValue.map(v => v.id));
                                    setSelectedLevelNames(
                                        levelOptions
                                            .filter(l => selectedIds.has(l.id))
                                            .map(l => l.name)
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Applicable levels for this entity"
                                        helperText="Subset of the entity type levels. Roll-up order follows the type. Top PROJECT grain is always implicit."
                                    />
                                )}
                                sx={{ mb: 2 }}
                            />

                            {showingUnsavedDefaultLevels && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    These are the <strong>default levels from the entity type</strong> — they are
                                    shown for convenience and are <strong>not saved yet</strong>. You{' '}
                                    <strong>must click Save Levels</strong> before you can add instances.
                                </Alert>
                            )}
                            {!showingUnsavedDefaultLevels && hasUnsavedLevelChanges && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    Level selection has changed. You <strong>must click Save Levels</strong> before
                                    adding instances for newly selected levels.
                                </Alert>
                            )}

                            {config && (
                                <Box sx={{ mb: 3 }}>
                                    <MetricLevelHierarchyGraph
                                        config={config}
                                        entityName={entityName}
                                        height={300}
                                        levelCatalog={levelOptions}
                                    />
                                </Box>
                            )}
                            {selectedTypeLevels.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No levels selected. Save after choosing levels to start adding instances.
                                </Typography>
                            ) : (
                                selectedTypeLevels.map(level => {
                                    const instances = (config?.instances || []).filter(
                                        inst => inst.metric_level_id === level.id
                                    );
                                    const levelNotSaved = !config?.applicable_metric_level_ids?.includes(level.id);
                                    return (
                                        <Box key={level.id} sx={{ mb: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    L{level.level_number}: {level.display_label}
                                                    {level.is_period_based ? ' (period-based)' : ''}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => openAddDialog(level)}
                                                    disabled={levelNotSaved}
                                                >
                                                    Add Instance
                                                </Button>
                                            </Box>
                                            {levelNotSaved && (
                                                <Alert severity="warning" sx={{ mb: 1 }}>
                                                    {showingUnsavedDefaultLevels
                                                        ? 'Default level — click Save Levels before adding an instance.'
                                                        : 'Save levels before adding instances for this newly selected level.'}
                                                </Alert>
                                            )}
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Name</TableCell>
                                                            <TableCell>Start</TableCell>
                                                            <TableCell>End</TableCell>
                                                            <TableCell>Parent</TableCell>
                                                            <TableCell align="right">Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {instances.length === 0 ? (
                                                            <TableRow>
                                                                <TableCell colSpan={5}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        No instances yet.
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ) : (
                                                            instances.map(inst => (
                                                                <TableRow key={inst.id}>
                                                                    <TableCell>{inst.name}</TableCell>
                                                                    <TableCell>{inst.start_date || '-'}</TableCell>
                                                                    <TableCell>{inst.end_date || '-'}</TableCell>
                                                                    <TableCell>{inst.parent_instance_name || '-'}</TableCell>
                                                                    <TableCell align="right">
                                                                        <IconButton size="small" onClick={() => openEditDialog(inst)}>
                                                                            <EditIcon fontSize="small" />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleDeleteInstance(inst)}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    );
                                })
                            )}
                        </>
                    )}
                </CardContent>
                </Collapse>
            </Card>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>{editMode === 'edit' ? 'Edit Instance' : 'Add Instance'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {dialogError && (
                            <Alert severity="error" onClose={() => setDialogError(null)}>
                                {dialogError}
                            </Alert>
                        )}
                        <FormControl fullWidth required>
                            <InputLabel id="metric-level-select-label">Metric Level</InputLabel>
                            <Select
                                labelId="metric-level-select-label"
                                label="Metric Level"
                                value={form.metric_level_id}
                                onChange={(e) =>
                                    setForm(prev => ({
                                        ...prev,
                                        metric_level_id: e.target.value as number,
                                        parent_instance_id: ''
                                    }))
                                }
                                disabled={editMode === 'edit'}
                            >
                                {(config?.applicable_metric_levels || []).map(level => (
                                    <MenuItem key={level.id} value={level.id}>
                                        L{level.level_number}: {level.display_label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Name"
                            required
                            fullWidth
                            value={form.name}
                            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                        />
                        {(
                            <>
                                <TextField
                                    label="Start Date (optional)"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={form.start_date}
                                    onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                                    helperText={
                                        selectedFormLevel?.is_period_based
                                            ? 'Optional. Metric period is entered on metric upload, not here.'
                                            : undefined
                                    }
                                />
                                <TextField
                                    label="End Date (optional)"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={form.end_date}
                                    onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                                    error={Boolean(
                                        form.start_date &&
                                            form.end_date &&
                                            form.end_date < form.start_date
                                    )}
                                    helperText={
                                        form.start_date &&
                                        form.end_date &&
                                        form.end_date < form.start_date
                                            ? 'End date must be on or after start date'
                                            : undefined
                                    }
                                />
                            </>
                        )}
                        <FormControl fullWidth>
                            <InputLabel id="parent-instance-label">Parent Instance</InputLabel>
                            <Select
                                labelId="parent-instance-label"
                                label="Parent Instance"
                                value={form.parent_instance_id}
                                onChange={(e) =>
                                    setForm(prev => ({ ...prev, parent_instance_id: e.target.value as string }))
                                }
                            >
                                <MenuItem value="">
                                    <em>None (rolls to PROJECT)</em>
                                </MenuItem>
                                {parentOptions.map(parent => (
                                    <MenuItem key={parent.id} value={parent.id}>
                                        {parent.metric_level_display_label || parent.metric_level_name}: {parent.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveInstance}
                        disabled={instanceSaving || !form.name.trim() || form.metric_level_id === ''}
                    >
                        {instanceSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <MetricsTemplateDownloadDialog
                open={templateDialogOpen}
                onClose={() => setTemplateDialogOpen(false)}
                entityId={entityId}
                entityName={entityName}
                fallbackLevels={levelOptions}
            />
        </Grid>
    );
};

export default ReviewEntityMetricLevelsPanel;
